/**
 * Custom Block Registration System
 * 
 * This is a custom implementation inspired by WooCommerce's block registration pattern.
 * It demonstrates how to restrict blocks to specific parent contexts and handle
 * different editor environments.
 */

import {
	registerBlockType,
	registerBlockVariation,
	unregisterBlockType,
	unregisterBlockVariation,
} from '@wordpress/blocks';
import { subscribe, select } from '@wordpress/data';
import { store as editorStore } from '@wordpress/editor';

/**
 * Custom Block Registration Manager
 * 
 * Manages block registration across different editor contexts with support for:
 * - Ancestor-based restrictions
 * - Context-aware registration (post editor vs site editor)
 * - Template-specific behavior
 * - Block variations
 */
export class CustomBlockRegistrationManager {
	constructor() {
		/** Track which blocks have been registered */
		this.registeredBlocks = new Set();
		
		/** Store for registered block configurations */
		this.blockConfigs = new Map();
		
		/** Current template ID being edited */
		this.currentTemplateId = null;
		
		/** Current post type being edited */
		this.currentPostType = null;
		
		/** Whether the manager has been initialized */
		this.initialized = false;

		this.initializeManager();
	}

	/**
	 * Get singleton instance
	 */
	static getInstance() {
		if (!CustomBlockRegistrationManager.instance) {
			CustomBlockRegistrationManager.instance = new CustomBlockRegistrationManager();
		}
		return CustomBlockRegistrationManager.instance;
	}

	/**
	 * Initialize the manager and set up listeners
	 */
	initializeManager() {
		if (this.initialized) {
			return;
		}

		// Get initial context
		this.updateEditorContext();

		let previousTemplateId = this.currentTemplateId;
		let previousPostType = this.currentPostType;

		// Subscribe to editor changes to handle dynamic registration
		const unsubscribe = subscribe(() => {
			this.updateEditorContext();
			
			// Only re-register if context actually changed
			if (
				this.currentTemplateId !== previousTemplateId ||
				this.currentPostType !== previousPostType
			) {
				previousTemplateId = this.currentTemplateId;
				previousPostType = this.currentPostType;
				this.reregisterBlocks();
				unsubscribe();
			}
		});

		this.initialized = true;
	}

	/**
	 * Update current editor context (template, post type, etc.)
	 */
	updateEditorContext() {
		// Check if we're in the site editor
		const editSiteStore = select('core/edit-site');
		if (editSiteStore) {
			const editedPostId = editSiteStore.getEditedPostId?.();
			this.currentTemplateId = editedPostId || null;
		} else {
			this.currentTemplateId = null;
		}

		// Get current post type from the editor
		const currentPostType = select(editorStore)?.getCurrentPostType?.();
		this.currentPostType = currentPostType || null;
	}

	/**
	 * Check if block has been registered
	 */
	hasBeenRegistered(key) {
		return this.registeredBlocks.has(key);
	}

	/**
	 * Check if block should be registered in current context
	 */
	shouldRegisterBlock(config) {
		const {
			isAvailableInPostEditor = false,
			isAvailableInPageEditor = false,
		} = config;

		const editSiteStore = select('core/edit-site');

		// If we're in the site editor, always register
		if (editSiteStore) {
			return true;
		}

		// In post editor, check if block is allowed
		if (this.currentPostType === 'post' && !isAvailableInPostEditor) {
			return false;
		}

		// In page editor, check if block is allowed
		if (this.currentPostType === 'page' && !isAvailableInPageEditor) {
			return false;
		}

		return true;
	}

	/**
	 * Determine if ancestor restrictions should be removed
	 */
	shouldRemoveAncestor(config) {
		const { removeAncestorInTemplates = [] } = config;
		const editSiteStore = select('core/edit-site');

		// Only consider removing ancestors in site editor
		if (!editSiteStore || !this.currentTemplateId) {
			return false;
		}

		// Check if current template matches any of the specified templates
		return removeAncestorInTemplates.some(templatePattern =>
			this.currentTemplateId?.includes(templatePattern)
		);
	}

	/**
	 * Get effective ancestor list for current context
	 */
	getEffectiveAncestors(config) {
		const { requiredAncestors, settings } = config;

		// If we should remove ancestors, return undefined
		if (this.shouldRemoveAncestor(config)) {
			return undefined;
		}

		// Use custom ancestors if provided, otherwise use block's ancestors
		return requiredAncestors || settings.ancestor;
	}

	/**
	 * Register a single block
	 */
	registerBlock(config, forceReregister = false) {
		const {
			blockName,
			settings,
			isVariationBlock = false,
			variationName,
		} = config;

		try {
			const key = variationName || blockName;

			// Skip if already registered (unless forcing re-registration)
			if (this.hasBeenRegistered(key) && !forceReregister) {
				return;
			}

			// Check if block should be registered in current context
			if (!this.shouldRegisterBlock(config)) {
				return;
			}

			if (isVariationBlock && variationName) {
				// Register as a block variation
				registerBlockVariation(blockName, settings);
			} else {
				// Register as a full block with appropriate ancestor constraints
				const ancestor = this.getEffectiveAncestors(config);

				registerBlockType(blockName, {
					...settings,
					ancestor,
				});
			}

			this.registeredBlocks.add(key);
		} catch (error) {
			console.error(`Failed to register block ${blockName}:`, error);
		}
	}

	/**
	 * Unregister a block
	 */
	unregisterBlock(config) {
		const { blockName, isVariationBlock = false, variationName } = config;

		try {
			const key = variationName || blockName;

			if (!this.hasBeenRegistered(key)) {
				return;
			}

			if (isVariationBlock && variationName) {
				unregisterBlockVariation(blockName, variationName);
			} else {
				unregisterBlockType(blockName);
			}

			this.registeredBlocks.delete(key);
		} catch (error) {
			console.error(`Failed to unregister block ${blockName}:`, error);
		}
	}

	/**
	 * Store the last known ancestor state for each block
	 */
	getBlockAncestorState(config) {
		const ancestor = this.getEffectiveAncestors(config);
		return JSON.stringify(ancestor);
	}

	/**
	 * Re-register all blocks based on current context
	 */
	reregisterBlocks() {
		this.blockConfigs.forEach((config) => {
			const key = config.variationName || config.blockName;
			const isRegistered = this.hasBeenRegistered(key);
			const shouldBeRegistered = this.shouldRegisterBlock(config);
			
			// Get current and stored ancestor state
			const currentAncestorState = this.getBlockAncestorState(config);
			const storedAncestorState = config._lastAncestorState;

			// Check if ancestor restrictions have changed
			const ancestorChanged = isRegistered && 
				storedAncestorState !== undefined && 
				currentAncestorState !== storedAncestorState;

			// Unregister if:
			// 1. Registered but shouldn't be in current context, OR
			// 2. Ancestor restrictions have changed (need to re-register)
			if (isRegistered && (!shouldBeRegistered || ancestorChanged)) {
				this.unregisterBlock(config);
			}

			// Register if:
			// 1. Not registered but should be, OR
			// 2. Was unregistered due to ancestor change
			if ((!isRegistered || ancestorChanged) && shouldBeRegistered) {
				config._lastAncestorState = currentAncestorState;
				this.registerBlock(config, true);
			}
		});
	}

	/**
	 * Register a new block configuration
	 * 
	 * Main entry point for registering blocks with this manager
	 */
	registerBlockConfig(config) {
		const key = config.variationName || config.blockName;
		
		// Store initial ancestor state
		config._lastAncestorState = this.getBlockAncestorState(config);
		
		this.blockConfigs.set(key, config);
		this.registerBlock(config);
	}

	/**
	 * Unregister a block configuration
	 */
	unregisterBlockConfig(blockName, variationName) {
		const key = variationName || blockName;
		const config = this.blockConfigs.get(key);

		if (config) {
			this.unregisterBlock(config);
			this.blockConfigs.delete(key);
		}
	}

	/**
	 * Get all registered block names
	 */
	getRegisteredBlocks() {
		return Array.from(this.registeredBlocks);
	}

	/**
	 * Check if a specific block is registered
	 */
	isBlockRegistered(blockName) {
		return this.registeredBlocks.has(blockName);
	}
}

/**
 * Register a custom block type with context-aware restrictions
 * 
 * This function provides a simple interface for registering blocks that:
 * - Only appear in specific parent contexts (using ancestor restrictions)
 * - Behave differently in site editor vs post/page editor
 * - Can have template-specific behavior
 * 
 * @param {string|Object} blockNameOrMetadata - Either a string block name or block metadata object
 * @param {Object} settings - Optional settings for block registration
 * @param {boolean} settings.isVariationBlock - Whether this block is a variation
 * @param {string} settings.variationName - The name of the variation if applicable
 * @param {boolean} settings.isAvailableInPostEditor - Whether the block should be available in post editor
 * @param {boolean} settings.isAvailableInPageEditor - Whether the block should be available in page editor
 * @param {Array<string>} settings.requiredAncestors - Custom ancestors to enforce in post/page editor
 * @param {Array<string>} settings.removeAncestorInTemplates - Whether to remove ancestor restrictions in specific templates
 * 
 * @example
 * ```javascript
 * // Register a block that only works inside a "custom-parent" block
 * registerCustomBlockType({
 *     name: 'my-plugin/child-block',
 *     title: 'Child Block',
 *     category: 'widgets',
 *     ancestor: ['my-plugin/custom-parent'],
 *     edit: () => <div>Edit View</div>,
 *     save: () => <div>Saved View</div>
 * }, {
 *     removeAncestorInTemplates: ['single-post']
 * });
 * ```
 */
export const registerCustomBlockType = (blockNameOrMetadata, settings = {}) => {
	// Extract block name
	const blockName =
		typeof blockNameOrMetadata === 'string'
			? blockNameOrMetadata
			: blockNameOrMetadata.name;

	if (!blockName) {
		console.error(
			'registerCustomBlockType: Block name is required for registration'
		);
		return;
	}

	// Extract metadata if blockNameOrMetadata is an object
	const metadataWithoutName =
		typeof blockNameOrMetadata === 'string'
			? {}
			: (() => {
					const { name, ...metadata } = blockNameOrMetadata;
					return metadata;
			  })();

	// Separate custom settings from block settings
	const {
		isVariationBlock,
		variationName,
		isAvailableInPostEditor,
		isAvailableInPageEditor,
		requiredAncestors,
		removeAncestorInTemplates,
		...blockSettings
	} = {
		...metadataWithoutName,
		...settings,
	};

	// Build internal configuration
	const config = {
		blockName,
		settings: blockSettings,
		isVariationBlock: isVariationBlock ?? false,
		variationName: variationName ?? undefined,
		isAvailableInPostEditor: isAvailableInPostEditor ?? false,
		isAvailableInPageEditor: isAvailableInPageEditor ?? false,
		requiredAncestors: requiredAncestors ?? undefined,
		removeAncestorInTemplates: removeAncestorInTemplates ?? [],
	};

	// Register with the manager
	CustomBlockRegistrationManager.getInstance().registerBlockConfig(config);
};

/**
 * Unregister a custom block type
 * 
 * @param {string} blockName - The name of the block to unregister
 * @param {string} variationName - Optional variation name if unregistering a variation
 */
export const unregisterCustomBlockType = (blockName, variationName) => {
	CustomBlockRegistrationManager.getInstance().unregisterBlockConfig(
		blockName,
		variationName
	);
};

/**
 * Check if a custom block is currently registered
 * 
 * @param {string} blockName - The name of the block to check
 * @returns {boolean} Whether the block is registered
 */
export const isCustomBlockRegistered = (blockName) => {
	return CustomBlockRegistrationManager.getInstance().isBlockRegistered(blockName);
};

/**
 * Get all currently registered custom blocks
 * 
 * @returns {Array<string>} Array of registered block names
 */
export const getRegisteredCustomBlocks = () => {
	return CustomBlockRegistrationManager.getInstance().getRegisteredBlocks();
};

